/**
 * Grid to geometry.
 *
 * A 140x140 level is nearly twenty thousand cells; a mesh per cell would be a
 * draw call per cell. Everything static is therefore merged into one indexed
 * mesh per chunk of `TUNING.render.chunkCells` cells, with a material group per
 * surface type. A visible chunk costs a handful of draw calls, and an unwanted
 * chunk costs one `visible = false`.
 *
 * UVs are world-space distances divided by a per-surface repeat length, so the
 * wallpaper is the same size in every room and stays continuous across the
 * chunk seams instead of being stretched over the whole floor.
 */

import * as THREE from 'three';
import { TUNING } from '../core/config';
import { hash2 } from '../core/math';
import { Cell, type GeneratedLevel, type Grid, type LevelSpec, type LightSpot } from '../core/types';
import type { MaterialSet } from './api';

/* Material slots. The order here is the order of a chunk mesh's material array. */
const S_FLOOR = 0;
const S_CEILING = 1;
const S_WALL = 2;
const S_TRIM = 3;
const S_PILLAR = 4;
const S_FRAME = 5;
const S_PANEL = 6;
const S_PANEL_DEAD = 7;
const SLOT_COUNT = 8;

/** Metres of world per texture repeat. Roughly one repeat per metre. */
const REPEAT = {
  floor: 1.25,
  wall: 1.4,
  trim: 1.0,
  pillar: 1.2,
  frame: 1.0,
  water: 2.4,
};

/** Skirting board. A shallow proud band with a lit top edge, not just a stripe. */
const TRIM_HEIGHT = 0.17;
const TRIM_PROUD = 0.03;

/** Door casing. */
const CASING_WIDTH = 0.12;
const CASING_PROUD = 0.05;

/** Fluorescent troffer dimensions, shared with the light rig. */
export const FIXTURE = {
  // Square, not a 2x4 troffer. The ceiling everybody pictures is a grid of
  // square recessed panels roughly two ceiling tiles across, and it is one of
  // the two or three details that make a yellow room read as *the* yellow room.
  panelWidth: 1.18,
  panelDepth: 1.18,
  /** Housing overhang past the panel on each side. */
  housingMargin: 0.09,
  /** How far the housing hangs below the ceiling. */
  housingDrop: 0.075,
};

/** Y of the emissive face of a ceiling fixture. */
/**
 * How far a `Cell.Open` cell's walls carry on above the ceiling line.
 *
 * An open cell has no ceiling, so without a collar above it the player looks
 * straight out over the top of the building. Indoors it only has to be tall
 * enough to close the view, and four metres does that from any floor position.
 *
 * Outdoors it is not a patch, it is the skyline. Level 11's premise is a city
 * that does not end, and the wiki describes buildings tall enough to lose the
 * top of; at the indoor cap its facades stop at 8.6 m, which is a low-rise
 * retail park. It is also the cheapest possible way to buy that: the collar is
 * wall quads merged into the chunk slots that are already being drawn, so it
 * costs triangles — which this game has thousands of and can afford tens of
 * thousands more — and not a single extra draw call.
 *
 * NOTE for Level 94: `eavesHeight()` in `render/propsubhouse.ts` mirrors this
 * function for the `motion` case, because every roof, chimney and upper window
 * on that level is authored to sit on top of the collar. The two must change
 * together or every roof on the level floats.
 */
export function collarHeight(spec: LevelSpec): number {
  if (spec.generator === 'city') return Math.max(8, Math.min(26, spec.ceilingHeight * 3.4));
  return Math.min(4, spec.ceilingHeight * 1.3);
}

export function fixturePanelY(ceilingHeight: number): number {
  return ceilingHeight - FIXTURE.housingDrop - 0.006;
}

/**
 * Deterministic quarter-turn per fixture. Without it every troffer points the
 * same way and the ceiling reads as a printed pattern rather than a building.
 */
export function fixtureRotation(grid: Grid, spot: LightSpot, seed: number): number {
  const c = grid.worldToCell(spot.pos.x, spot.pos.z);
  return hash2(c.cx, c.cy, seed) < 0.5 ? 0 : Math.PI / 2;
}

/** One batched slice of the level. */
export interface LevelChunk {
  /** Static geometry plus any dynamic props the light rig parents here. */
  readonly group: THREE.Group;
  /** World bounds, for the frustum test. */
  readonly box: THREE.Box3;
  readonly centerX: number;
  readonly centerZ: number;
  readonly halfX: number;
  readonly halfZ: number;
}

export interface LevelGeometry {
  readonly chunks: readonly LevelChunk[];
  /** Shared plane for the per-fixture flicker panels the light rig creates. */
  readonly panelGeometry: THREE.BufferGeometry;
  /** Chunk containing a world point, for parenting dynamic props. */
  chunkAt(wx: number, wz: number): LevelChunk | null;
  dispose(): void;
}

/** Accumulates one material slot's quads before they are merged. */
class QuadSoup {
  readonly pos: number[] = [];
  readonly nrm: number[] = [];
  readonly uv: number[] = [];
  quads = 0;
}

/**
 * Emits axis-aligned quads into per-slot buffers. Every helper below is written
 * so that a,b,c,d wind counter-clockwise seen from the normal side, which is
 * what back-face culling needs.
 */
class ChunkBuilder {
  private readonly slots: QuadSoup[] = [];

  constructor(slotCount: number) {
    for (let i = 0; i < slotCount; i++) this.slots.push(new QuadSoup());
  }

  get empty(): boolean {
    for (const s of this.slots) if (s.quads > 0) return false;
    return true;
  }

  /** a,b,c,d counter-clockwise from the normal side; UVs a=(u0,v0) .. c=(u1,v1). */
  quad(
    slot: number,
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number,
    dx: number,
    dy: number,
    dz: number,
    nx: number,
    ny: number,
    nz: number,
    u0: number,
    v0: number,
    u1: number,
    v1: number
  ): void {
    const s = this.slots[slot]!;
    s.pos.push(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz);
    s.nrm.push(nx, ny, nz, nx, ny, nz, nx, ny, nz, nx, ny, nz);
    s.uv.push(u0, v0, u1, v0, u1, v1, u0, v1);
    s.quads++;
  }

  /** Horizontal quad facing up. */
  upQuad(slot: number, x0: number, z0: number, x1: number, z1: number, y: number, u0: number, v0: number, u1: number, v1: number): void {
    this.quad(slot, x0, y, z1, x1, y, z1, x1, y, z0, x0, y, z0, 0, 1, 0, u0, v0, u1, v1);
  }

  /** Horizontal quad facing down. */
  downQuad(slot: number, x0: number, z0: number, x1: number, z1: number, y: number, u0: number, v0: number, u1: number, v1: number): void {
    this.quad(slot, x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1, 0, -1, 0, u0, v0, u1, v1);
  }

  /**
   * Vertical quad. `(ax,az) -> (bx,bz)` is the horizontal run and must be wound
   * so that `(-runZ, runX)` matches the requested normal; the callers below use
   * the direction table for that.
   */
  wallQuad(
    slot: number,
    ax: number,
    az: number,
    bx: number,
    bz: number,
    y0: number,
    y1: number,
    nx: number,
    nz: number,
    u0: number,
    u1: number,
    v0: number,
    v1: number
  ): void {
    this.quad(slot, ax, y0, az, bx, y0, bz, bx, y1, bz, ax, y1, az, nx, 0, nz, u0, v0, u1, v1);
  }

  /** The proud top edge of a skirting board, from the wall plane out by (ox, oz). */
  capStrip(slot: number, ax: number, az: number, bx: number, bz: number, ox: number, oz: number, y: number, u0: number, u1: number): void {
    this.quad(slot, ax + ox, y, az + oz, bx + ox, y, bz + oz, bx, y, bz, ax, y, az, 0, 1, 0, u0, 0, u1, 1);
  }

  /** The four outward-facing sides of an axis-aligned footprint. */
  boxSides(slot: number, x0: number, z0: number, x1: number, z1: number, y0: number, y1: number, repeat: number): void {
    const v0 = y0 / repeat;
    const v1 = y1 / repeat;
    this.wallQuad(slot, x1, z1, x1, z0, y0, y1, 1, 0, z1 / repeat, z0 / repeat, v0, v1);
    this.wallQuad(slot, x0, z0, x0, z1, y0, y1, -1, 0, z0 / repeat, z1 / repeat, v0, v1);
    this.wallQuad(slot, x0, z1, x1, z1, y0, y1, 0, 1, x0 / repeat, x1 / repeat, v0, v1);
    this.wallQuad(slot, x1, z0, x0, z0, y0, y1, 0, -1, x1 / repeat, x0 / repeat, v0, v1);
  }

  /** A closed box. Used for door casings, where every face can be seen. */
  boxSolid(slot: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, repeat: number): void {
    this.boxSides(slot, x0, z0, x1, z1, y0, y1, repeat);
    this.upQuad(slot, x0, z0, x1, z1, y1, x0 / repeat, z1 / repeat, x1 / repeat, z0 / repeat);
    this.downQuad(slot, x0, z0, x1, z1, y0, x0 / repeat, z0 / repeat, x1 / repeat, z1 / repeat);
  }

  /**
   * Merge into one indexed geometry with a group per non-empty slot. Returns
   * null when the chunk holds nothing, so empty regions cost no GPU memory.
   */
  finish(): THREE.BufferGeometry | null {
    let verts = 0;
    let quads = 0;
    for (const s of this.slots) {
      verts += s.quads * 4;
      quads += s.quads;
    }
    if (quads === 0) return null;

    const pos = new Float32Array(verts * 3);
    const nrm = new Float32Array(verts * 3);
    const uv = new Float32Array(verts * 2);
    const index = verts > 65535 ? new Uint32Array(quads * 6) : new Uint16Array(quads * 6);

    const geo = new THREE.BufferGeometry();
    let vBase = 0;
    let f3 = 0;
    let f2 = 0;
    let ii = 0;
    for (let slot = 0; slot < this.slots.length; slot++) {
      const s = this.slots[slot]!;
      if (s.quads === 0) continue;
      pos.set(s.pos, f3);
      nrm.set(s.nrm, f3);
      uv.set(s.uv, f2);
      f3 += s.pos.length;
      f2 += s.uv.length;

      const start = ii;
      for (let q = 0; q < s.quads; q++) {
        const v = vBase + q * 4;
        index[ii++] = v;
        index[ii++] = v + 1;
        index[ii++] = v + 2;
        index[ii++] = v;
        index[ii++] = v + 2;
        index[ii++] = v + 3;
      }
      geo.addGroup(start, ii - start, slot);
      vBase += s.quads * 4;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('normal', new THREE.BufferAttribute(nrm, 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    geo.setIndex(new THREE.BufferAttribute(index, 1));
    geo.computeBoundingSphere();
    geo.computeBoundingBox();
    return geo;
  }
}

/**
 * For a walkable cell, the four boundaries it can have with a solid neighbour.
 * `nx/nz` is the face normal, which points back into the walkable cell.
 */
const FACES: ReadonlyArray<{ dx: number; dy: number; nx: number; nz: number }> = [
  { dx: 1, dy: 0, nx: -1, nz: 0 },
  { dx: -1, dy: 0, nx: 1, nz: 0 },
  { dx: 0, dy: 1, nx: 0, nz: -1 },
  { dx: 0, dy: -1, nx: 0, nz: 1 },
];

/**
 * Endpoints of one cell boundary, wound to match `FACES[f].nx/nz`. Build-time
 * scratch: geometry is assembled once per level, never per frame.
 */
const RUN = { ax: 0, az: 0, bx: 0, bz: 0, u0: 0, u1: 0 };

function faceRun(f: number, x0: number, z0: number, x1: number, z1: number): void {
  if (f === 0) {
    RUN.ax = x1;
    RUN.az = z0;
    RUN.bx = x1;
    RUN.bz = z1;
    RUN.u0 = z0;
    RUN.u1 = z1;
  } else if (f === 1) {
    RUN.ax = x0;
    RUN.az = z1;
    RUN.bx = x0;
    RUN.bz = z0;
    RUN.u0 = z1;
    RUN.u1 = z0;
  } else if (f === 2) {
    RUN.ax = x1;
    RUN.az = z1;
    RUN.bx = x0;
    RUN.bz = z1;
    RUN.u0 = x1;
    RUN.u1 = x0;
  } else {
    RUN.ax = x0;
    RUN.az = z0;
    RUN.bx = x1;
    RUN.bz = z0;
    RUN.u0 = x0;
    RUN.u1 = x1;
  }
}

/** Build the whole level's static geometry. */
export function buildLevelGeometry(level: GeneratedLevel, materials: MaterialSet): LevelGeometry {
  const { grid, spec } = level;
  const cs = grid.cellSize;
  const step = Math.max(2, Math.floor(TUNING.render.chunkCells));
  const nx = Math.ceil(grid.w / step);
  const nz = Math.ceil(grid.h / step);

  const slotMats: THREE.Material[] = [
    materials.floor,
    materials.ceiling,
    materials.wall,
    materials.trim,
    materials.pillar,
    materials.doorframe,
    materials.lightPanel,
    materials.lightPanelDead,
  ];

  // Fixtures are baked into the chunk they sit in: the housing always, and the
  // emissive panel too unless the fixture flickers and so needs its own material.
  const fixtures: LightSpot[][] = [];
  for (let i = 0; i < nx * nz; i++) fixtures.push([]);
  for (const spot of level.lights) {
    const c = grid.worldToCell(spot.pos.x, spot.pos.z);
    const bx = Math.min(nx - 1, Math.max(0, Math.floor(c.cx / step)));
    const bz = Math.min(nz - 1, Math.max(0, Math.floor(c.cy / step)));
    fixtures[bz * nx + bx]!.push(spot);
  }

  const chunks: LevelChunk[] = [];
  const owned: THREE.BufferGeometry[] = [];
  const waterMat = materials.water;
  const waterY = spec.waterDepth ?? 0.12;

  for (let bz = 0; bz < nz; bz++) {
    for (let bx = 0; bx < nx; bx++) {
      const cx0 = bx * step;
      const cy0 = bz * step;
      const cx1 = Math.min(grid.w, cx0 + step);
      const cy1 = Math.min(grid.h, cy0 + step);

      const b = new ChunkBuilder(SLOT_COUNT);
      const water = new ChunkBuilder(1);
      emitCells(b, water, grid, spec, cx0, cy0, cx1, cy1, waterY, waterMat !== null);
      for (const spot of fixtures[bz * nx + bx]!) {
        emitFixture(b, grid, spec, spot, level.seed);
      }

      const group = new THREE.Group();
      group.matrixAutoUpdate = false;

      const geo = b.finish();
      if (geo) {
        owned.push(geo);
        const mesh = new THREE.Mesh(geo, slotMats);
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        group.add(mesh);
      }
      if (waterMat) {
        const wgeo = water.finish();
        if (wgeo) {
          owned.push(wgeo);
          const wmesh = new THREE.Mesh(wgeo, waterMat);
          wmesh.matrixAutoUpdate = false;
          wmesh.updateMatrix();
          // Drawn after the opaque floor beneath it.
          wmesh.renderOrder = 2;
          group.add(wmesh);
        }
      }
      group.updateMatrix();

      const x0 = cx0 * cs;
      const z0 = cy0 * cs;
      const x1 = cx1 * cs;
      const z1 = cy1 * cs;
      chunks.push({
        group,
        box: new THREE.Box3(new THREE.Vector3(x0, -0.2, z0), new THREE.Vector3(x1, spec.ceilingHeight + 0.4, z1)),
        centerX: (x0 + x1) / 2,
        centerZ: (z0 + z1) / 2,
        halfX: (x1 - x0) / 2,
        halfZ: (z1 - z0) / 2,
      });
    }
  }

  const panelGeometry = new THREE.PlaneGeometry(FIXTURE.panelWidth, FIXTURE.panelDepth);

  return {
    chunks,
    panelGeometry,
    chunkAt(wx: number, wz: number): LevelChunk | null {
      const bx = Math.floor(wx / cs / step);
      const bz = Math.floor(wz / cs / step);
      if (bx < 0 || bz < 0 || bx >= nx || bz >= nz) return null;
      return chunks[bz * nx + bx] ?? null;
    },
    dispose(): void {
      for (const g of owned) g.dispose();
      owned.length = 0;
      panelGeometry.dispose();
      for (const c of chunks) c.group.clear();
    },
  };
}

/** Floors, ceilings, walls, skirting, pillars, doorways and water for one chunk. */
function emitCells(
  b: ChunkBuilder,
  water: ChunkBuilder,
  grid: Grid,
  spec: LevelSpec,
  cx0: number,
  cy0: number,
  cx1: number,
  cy1: number,
  waterY: number,
  wantWater: boolean
): void {
  const cs = grid.cellSize;
  const ceilY = spec.ceilingHeight;
  const doorY = Math.min(2.15, ceilY - 0.35);
  // Ceiling tiles line up with the cell grid; anything else reads as a smear.
  const ceilRepeat = cs / 2;
  const shaftY = collarHeight(spec);

  for (let cy = cy0; cy < cy1; cy++) {
    for (let cx = cx0; cx < cx1; cx++) {
      const cell = grid.at(cx, cy);
      if (cell === Cell.Void || cell === Cell.Wall) continue;

      const x0 = cx * cs;
      const z0 = cy * cs;
      const x1 = x0 + cs;
      const z1 = z0 + cs;

      b.upQuad(S_FLOOR, x0, z0, x1, z1, 0, x0 / REPEAT.floor, z1 / REPEAT.floor, x1 / REPEAT.floor, z0 / REPEAT.floor);

      if (wantWater && cell === Cell.Water) {
        water.upQuad(0, x0, z0, x1, z1, waterY, x0 / REPEAT.water, z1 / REPEAT.water, x1 / REPEAT.water, z0 / REPEAT.water);
      }

      const headed = cell === Cell.Doorway && emitDoorway(b, grid, cx, cy, cs, ceilY, doorY);
      if (cell !== Cell.Open && !headed) {
        b.downQuad(S_CEILING, x0, z0, x1, z1, ceilY, x0 / ceilRepeat, z0 / ceilRepeat, x1 / ceilRepeat, z1 / ceilRepeat);
      }

      if (cell === Cell.Pillar) {
        const mx = (x0 + x1) / 2;
        const mz = (z0 + z1) / 2;
        // Must be the collision half-width, not a modelling guess: a pillar
        // drawn narrower than its blocking box is a ring of invisible wall
        // around every column in the level.
        const h = cs * TUNING.player.pillarHalfFraction;
        const t = h + TRIM_PROUD;
        b.boxSides(S_PILLAR, mx - h, mz - h, mx + h, mz + h, TRIM_HEIGHT, ceilY, REPEAT.pillar);
        b.boxSides(S_TRIM, mx - t, mz - t, mx + t, mz + t, 0, TRIM_HEIGHT, REPEAT.trim);
        b.upQuad(S_TRIM, mx - t, mz - t, mx + t, mz + t, TRIM_HEIGHT, 0, 0, 1, 1);
      }

      // Wall faces are emitted from the walkable side so out-of-bounds edges are
      // sealed too, and so a face belongs to the chunk of the room that sees it.
      for (let f = 0; f < 4; f++) {
        const dir = FACES[f]!;
        const nxc = cx + dir.dx;
        const nyc = cy + dir.dy;
        const solid = !grid.inBounds(nxc, nyc) || grid.isSolid(nxc, nyc);

        // An Open cell has no ceiling, so it needs a collar above the ceiling
        // line or the player looks straight out of the building.
        if (cell === Cell.Open && grid.at(nxc, nyc) !== Cell.Open) {
          faceRun(f, x0, z0, x1, z1);
          b.wallQuad(
            S_WALL,
            RUN.ax,
            RUN.az,
            RUN.bx,
            RUN.bz,
            ceilY,
            ceilY + shaftY,
            dir.nx,
            dir.nz,
            RUN.u0 / REPEAT.wall,
            RUN.u1 / REPEAT.wall,
            ceilY / REPEAT.wall,
            (ceilY + shaftY) / REPEAT.wall
          );
        }
        if (!solid) continue;

        faceRun(f, x0, z0, x1, z1);
        const ax = RUN.ax;
        const az = RUN.az;
        const bxx = RUN.bx;
        const bzz = RUN.bz;
        const u0 = RUN.u0;
        const u1 = RUN.u1;

        b.wallQuad(
          S_WALL,
          ax,
          az,
          bxx,
          bzz,
          TRIM_HEIGHT,
          ceilY,
          dir.nx,
          dir.nz,
          u0 / REPEAT.wall,
          u1 / REPEAT.wall,
          TRIM_HEIGHT / REPEAT.wall,
          ceilY / REPEAT.wall
        );

        const ox = dir.nx * TRIM_PROUD;
        const oz = dir.nz * TRIM_PROUD;
        // Run the skirting a board's thickness past each end of its wall face.
        // The board stands `TRIM_PROUD` off the wall along its own normal, so
        // two runs meeting at a corner are offset from each other by exactly
        // that much and stop short, leaving a small square notch at the floor.
        // Overrunning closes it: at an internal corner the two boards overlap
        // in the void, and at an external one they wrap around it the way real
        // skirting does. The UVs are left alone — 3cm of stretch in a 1m repeat
        // is invisible, and matching them would break the run-to-run continuity.
        const rdx = bxx - ax;
        const rdz = bzz - az;
        const rlen = Math.hypot(rdx, rdz) || 1;
        const ex = (rdx / rlen) * TRIM_PROUD;
        const ez = (rdz / rlen) * TRIM_PROUD;
        b.wallQuad(
          S_TRIM,
          ax + ox - ex,
          az + oz - ez,
          bxx + ox + ex,
          bzz + oz + ez,
          0,
          TRIM_HEIGHT,
          dir.nx,
          dir.nz,
          u0 / REPEAT.trim,
          u1 / REPEAT.trim,
          0,
          1
        );
        b.capStrip(
          S_TRIM,
          ax - ex,
          az - ez,
          bxx + ex,
          bzz + ez,
          ox,
          oz,
          TRIM_HEIGHT,
          u0 / REPEAT.trim,
          u1 / REPEAT.trim
        );
      }
    }
  }
}

/**
 * Header and casings for a doorway cell. Returns true when a header was built,
 * in which case the caller skips the ceiling tile it would hide.
 */
function emitDoorway(b: ChunkBuilder, grid: Grid, cx: number, cy: number, cs: number, ceilY: number, doorY: number): boolean {
  const alongZ = grid.isSolid(cx - 1, cy) && grid.isSolid(cx + 1, cy);
  const alongX = !alongZ && grid.isSolid(cx, cy - 1) && grid.isSolid(cx, cy + 1);
  if (!alongZ && !alongX) return false;

  const x0 = cx * cs;
  const z0 = cy * cs;
  const x1 = x0 + cs;
  const z1 = z0 + cs;
  const openNeg = alongZ ? grid.isWalkable(cx, cy - 1) : grid.isWalkable(cx - 1, cy);
  const openPos = alongZ ? grid.isWalkable(cx, cy + 1) : grid.isWalkable(cx + 1, cy);
  if (!openNeg && !openPos) return false;

  const r = REPEAT.frame;
  // The header fills the wall above the opening. Its sides sit inside the
  // flanking wall cells, whose faces are already drawn, so they are left out.
  b.downQuad(S_FRAME, x0, z0, x1, z1, doorY, x0 / r, z0 / r, x1 / r, z1 / r);
  if (alongZ) {
    if (openNeg) b.wallQuad(S_FRAME, x1, z0, x0, z0, doorY, ceilY, 0, -1, x1 / r, x0 / r, doorY / r, ceilY / r);
    if (openPos) b.wallQuad(S_FRAME, x0, z1, x1, z1, doorY, ceilY, 0, 1, x0 / r, x1 / r, doorY / r, ceilY / r);
  } else {
    if (openNeg) b.wallQuad(S_FRAME, x0, z0, x0, z1, doorY, ceilY, -1, 0, z0 / r, z1 / r, doorY / r, ceilY / r);
    if (openPos) b.wallQuad(S_FRAME, x1, z1, x1, z0, doorY, ceilY, 1, 0, z1 / r, z0 / r, doorY / r, ceilY / r);
  }

  const w = CASING_WIDTH;
  const p = CASING_PROUD;
  if (alongZ) {
    if (openNeg) casing(b, x0, x1, z0 - p, z0, doorY, w, r, true);
    if (openPos) casing(b, x0, x1, z1, z1 + p, doorY, w, r, true);
  } else {
    if (openNeg) casing(b, x0 - p, x0, z0, z1, doorY, w, r, false);
    if (openPos) casing(b, x1, x1 + p, z0, z1, doorY, w, r, false);
  }
  return true;
}

/** Two jambs and a head, proud of the wall, around one mouth of a doorway. */
function casing(
  b: ChunkBuilder,
  x0: number,
  x1: number,
  z0: number,
  z1: number,
  doorY: number,
  w: number,
  repeat: number,
  alongZ: boolean
): void {
  if (alongZ) {
    b.boxSolid(S_FRAME, x0, 0, z0, x0 + w, doorY + w, z1, repeat);
    b.boxSolid(S_FRAME, x1 - w, 0, z0, x1, doorY + w, z1, repeat);
    b.boxSolid(S_FRAME, x0 + w, doorY, z0, x1 - w, doorY + w, z1, repeat);
  } else {
    b.boxSolid(S_FRAME, x0, 0, z0, x1, doorY + w, z0 + w, repeat);
    b.boxSolid(S_FRAME, x0, 0, z1 - w, x1, doorY + w, z1, repeat);
    b.boxSolid(S_FRAME, x0, doorY, z0 + w, x1, doorY + w, z1 - w, repeat);
  }
}

/** Housing for every fixture; the emissive panel too unless it needs to flicker. */
function emitFixture(b: ChunkBuilder, grid: Grid, spec: LevelSpec, spot: LightSpot, seed: number): void {
  const rot = fixtureRotation(grid, spot, seed);
  const halfX = (rot === 0 ? FIXTURE.panelWidth : FIXTURE.panelDepth) / 2;
  const halfZ = (rot === 0 ? FIXTURE.panelDepth : FIXTURE.panelWidth) / 2;
  const hx = halfX + FIXTURE.housingMargin;
  const hz = halfZ + FIXTURE.housingMargin;
  const x = spot.pos.x;
  const z = spot.pos.z;
  const y0 = spec.ceilingHeight - FIXTURE.housingDrop;
  const r = REPEAT.frame;

  b.boxSides(S_FRAME, x - hx, z - hz, x + hx, z + hz, y0, spec.ceilingHeight, r);
  b.downQuad(S_FRAME, x - hx, z - hz, x + hx, z + hz, y0, (x - hx) / r, (z - hz) / r, (x + hx) / r, (z + hz) / r);

  // Flickering fixtures get their own mesh so they can be driven individually.
  if (spot.state === 'flicker') return;
  const slot = spot.state === 'dead' ? S_PANEL_DEAD : S_PANEL;
  const py = fixturePanelY(spec.ceilingHeight);
  if (rot === 0) b.downQuad(slot, x - halfX, z - halfZ, x + halfX, z + halfZ, py, 0, 0, 1, 1);
  else b.downQuad(slot, x - halfX, z - halfZ, x + halfX, z + halfZ, py, 0, 1, 1, 0);
}
